import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  VideoCameraIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { BookingSettings, Appointment, AvailableSlot } from '../../types/appointments';
import { publicBookingApi, toIsoDay, toIsoMonth } from '../../services/appointmentService';
import { Button } from '../common/Button';
import LoadingScreen from '../common/LoadingScreen';
import { usePublicTheme } from '../../hooks/usePublicTheme';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';

interface BookingFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
  meetingType: 'in_person' | 'online';
}

const PublicBookingPage: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const { theme, toggle: toggleTheme } = usePublicTheme();
  
  // State
  const [settings, setSettings] = useState<BookingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'date' | 'time' | 'info' | 'confirm'>('date');
  
  // Dátum és idő
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // Napi szabad-időpont számok a hónapra (csak azok a napok kattinthatók, ahol van) – hónaponként gyorsítótárazva
  const [monthCache, setMonthCache] = useState<Record<string, Record<string, number>>>({});
  const monthKey = toIsoMonth(currentMonth);
  const monthDays: Record<string, number> | null = monthCache[monthKey] ?? null;
  const monthLoading = monthDays === null;
  useEffect(() => {
    if (!settings || !publicId || monthCache[monthKey]) return;
    let alive = true;
    publicBookingApi.month({ publicId, month: monthKey })
      .then(r => { if (alive) setMonthCache(prev => ({ ...prev, [monthKey]: r.data.days || {} })); })
      .catch((e) => { console.error('publicBookingMonth failed:', e); if (alive) setMonthCache(prev => ({ ...prev, [monthKey]: {} })); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, publicId, monthKey]);

  // Form adatok
  const [formData, setFormData] = useState<BookingFormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
    meetingType: 'in_person'
  });
  
  // Foglalás állapot
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  // Beállítások betöltése
  useEffect(() => {
    const loadSettings = async () => {
      if (!publicId) {
        setError('Érvénytelen foglalási link');
        setLoading(false);
        return;
      }

      try {
        const result = await publicBookingApi.get({ publicId });
        const bookingSettings = result.data.settings;
        setSettings(bookingSettings);
      } catch (error: any) {
        console.error('Error loading booking settings:', error);
        setError(error?.message && !/internal/i.test(error.message) ? error.message : 'Hiba történt a beállítások betöltése során');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [publicId]);

  // Meeting típus inicializálása a beállítások alapján
  useEffect(() => {
    if (settings?.meetingOptions) {
      // Hibrid: az ügyfél dönt (alap: személyes). Online/személyes: rögzített.
      const mo = settings.meetingOptions;
      const defaultType: 'in_person' | 'online' =
        !mo.enableOnlineMeeting ? 'in_person'
        : mo.defaultMeetingType === 'online' ? 'online'
        : 'in_person';
      setFormData(prev => ({ ...prev, meetingType: defaultType }));
    }
  }, [settings]);

  // Elérhető időpontok betöltése
  useEffect(() => {
    const loadAvailableSlots = async () => {
      if (!settings || !selectedDate) return;
      setSlotsLoading(true);
      try {
        // Szabad időpontok a szervertől (meglévő foglalások + Google Naptár foglaltság)
        const result = await publicBookingApi.slots({ publicId: publicId!, date: toIsoDay(selectedDate) });
        const slots = result.data.slots.map(sl => ({ time: sl.time, datetime: new Date(sl.datetime), isAvailable: sl.isAvailable }));
        setAvailableSlots(slots);
      } catch (error) {
        console.error('Error loading available slots:', error);
        setAvailableSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };

    loadAvailableSlots();
  }, [settings, selectedDate]);

  // Foglalási ablak: mától legfeljebb N napra előre
  const maxAdvanceDays = settings?.advanceBooking?.max ?? 60;
  const lastBookableDate = new Date(); lastBookableDate.setDate(lastBookableDate.getDate() + maxAdvanceDays);
  const canGoPrev = currentMonth > new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const canGoNext = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) <= lastBookableDate;

  // Naptár navigáció
  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  }, []);

  // Naptár napok generálása
  const generateCalendarDays = useCallback(() => {
    if (!settings) return [];

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const isCurrentMonth = current.getMonth() === month;
      const isToday = current.toDateString() === new Date().toDateString();
      const isSelected = selectedDate?.toDateString() === current.toDateString();
      const isAvailable = isCurrentMonth && !!monthDays && (monthDays[toIsoDay(current)] || 0) > 0;

      days.push({
        date: new Date(current),
        day: current.getDate(),
        isCurrentMonth,
        isToday,
        isSelected,
        isAvailable
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [currentMonth, settings, selectedDate, monthDays]);

  // Form kezelés
  const handleInputChange = useCallback((field: keyof BookingFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Foglalás leadása
  const handleSubmit = useCallback(async () => {
    if (!settings || !selectedSlot || !formData.name || !formData.email) return;

    setIsSubmitting(true);
    try {
      await publicBookingApi.create({
        publicId: publicId!,
        clientName: formData.name,
        clientEmail: formData.email,
        clientPhone: formData.phone,
        clientMessage: formData.message,
        appointmentDate: selectedSlot.datetime.toISOString(),
        meetingType: formData.meetingType === 'online' ? 'online' : 'in_person',
        baseUrl: window.location.origin,
      });

      setIsBooked(true);
      setCurrentStep('confirm');
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      const msg = error?.message && !/internal/i.test(error.message) ? error.message : 'Hiba történt a foglalás során. Kérjük, próbálja újra.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [settings, selectedSlot, formData]);

  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <div className="min-h-screen bg-[#f7f7f9] dark:bg-[#0b0d12] text-gray-900 dark:text-gray-100 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-[#12151c] rounded-xl border border-gray-200 dark:border-[#2a2f3c] shadow-sm p-8 max-w-md w-full text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Hiba történt</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Button
            variant="primary"
            onClick={() => window.location.reload()}
          >
            Újrapróbálás
          </Button>
        </div>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="min-h-screen bg-[#f7f7f9] dark:bg-[#0b0d12] text-gray-900 dark:text-gray-100">
      {/* Fejléc */}
      <div className="bg-white dark:bg-[#12151c] border-b border-gray-200 dark:border-[#2a2f3c] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="min-w-0 flex-1 text-center sm:text-left">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-50 tracking-tight truncate">
              {settings.title}
            </h1>
            {settings.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 truncate">{settings.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="flex-shrink-0 w-9 h-9 rounded-lg border border-gray-200 dark:border-[#2a2f3c] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center"
            title={theme === 'dark' ? 'Világos mód' : 'Sötét mód'}
            aria-label={theme === 'dark' ? 'Világos mód' : 'Sötét mód'}
          >
            {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Fő tartalom */}
      <div className="max-w-4xl mx-auto p-4 py-8">
        {!isBooked ? (
          <div className="bg-white dark:bg-[#12151c] rounded-xl border border-gray-200 dark:border-[#2a2f3c] shadow-sm overflow-hidden">
            {/* Lépés indikátor */}
            <div className="px-6 py-4 bg-primary-600">
              <div className="flex items-center justify-between">
                {[
                  { key: 'date', label: 'Dátum', icon: CalendarDaysIcon },
                  { key: 'time', label: 'Időpont', icon: ClockIcon },
                  { key: 'info', label: 'Adatok', icon: UserIcon },
                ].map((step, index) => (
                  <div key={step.key} className="flex items-center">
                    <div className={`
                      flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors
                      ${currentStep === step.key || (index < ['date', 'time', 'info'].indexOf(currentStep))
                        ? 'bg-white text-primary-600 border-white'
                        : 'bg-transparent text-white border-white/50'
                      }
                    `}>
                      <step.icon className="h-4 w-4" />
                    </div>
                    <span className="ml-2 text-white text-sm font-medium hidden sm:block">
                      {step.label}
                    </span>
                    {index < 2 && (
                      <div className="hidden sm:block w-8 h-0.5 bg-white/30 mx-4" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Tartalom */}
            <div className="p-6">
              {/* Dátum választás */}
              {currentStep === 'date' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Válassz dátumot
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      Időtartam: {settings.duration} perc
                    </p>
                  </div>

                  {/* Naptár fejléc */}
                  <div className="flex items-center justify-between">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateMonth('prev')}
                      disabled={!canGoPrev}
                      className="flex items-center space-x-1"
                    >
                      <ArrowLeftIcon className="h-4 w-4" />
                    </Button>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {currentMonth.toLocaleDateString('hu-HU', { 
                        year: 'numeric', 
                        month: 'long' 
                      })}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateMonth('next')}
                      disabled={!canGoNext}
                      className="flex items-center space-x-1"
                    >
                      <ArrowRightIcon className="h-4 w-4" />
                    </Button>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
                    Legfeljebb {maxAdvanceDays} napra előre foglalható (
                    {lastBookableDate.toLocaleDateString('hu-HU', { month: 'long', day: 'numeric' })}-ig).
                    {' '}A szürke napokon nincs szabad időpont.
                  </p>

                  {/* Naptár */}
                  <div className="relative">
                  {monthLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 dark:bg-[#12151c]/70 backdrop-blur-[1px]" aria-live="polite">
                      <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white dark:bg-[#12151c] border border-gray-200 dark:border-[#2a2f3c] shadow-sm text-sm font-medium text-gray-800 dark:text-gray-100">
                        <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-primary-600 animate-spin" />
                        Szabad napok betöltése…
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-7 gap-1">
                    {['V', 'H', 'K', 'Sz', 'Cs', 'P', 'Szo'].map((day, index) => (
                      <div key={`day-header-${index}`} className="p-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                        {day}
                      </div>
                    ))}
                    {generateCalendarDays().map((day, index) => (
                      <button
                        key={`calendar-day-${day.date.getTime()}`}
                        onClick={() => {
                          if (day.isAvailable) {
                            setSelectedDate(day.date);
                            setCurrentStep('time');
                          }
                        }}
                        disabled={!day.isAvailable || monthLoading}
                        className={`
                          p-2 text-sm rounded-lg transition-all
                          ${monthLoading && day.isCurrentMonth ? 'animate-pulse bg-gray-100 dark:bg-gray-800 text-transparent' : ''}
                          ${!day.isCurrentMonth ? 'text-gray-300 dark:text-gray-600' : ''}
                          ${day.isToday ? 'font-bold' : ''}
                          ${day.isSelected ? 'bg-primary-600 text-white' : ''}
                          ${day.isAvailable && !day.isSelected ? 'hover:bg-primary-50 dark:hover:bg-primary-900/20 text-gray-900 dark:text-gray-100' : ''}
                          ${!day.isAvailable && day.isCurrentMonth && !monthLoading ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : ''}
                        `}
                      >
                        {day.day}
                      </button>
                    ))}
                  </div>
                  </div>
                </div>
              )}

              {/* Időpont választás */}
              {currentStep === 'time' && selectedDate && (
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Válassz időpontot
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {selectedDate.toLocaleDateString('hu-HU', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>

                  {slotsLoading ? (
                    <div className="relative" aria-busy="true">
                      <div className="absolute inset-0 z-10 flex items-center justify-center">
                        <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white dark:bg-[#12151c] border border-gray-200 dark:border-[#2a2f3c] shadow-sm text-sm font-medium text-gray-800 dark:text-gray-100">
                          <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-primary-600 animate-spin" />
                          Szabad időpontok betöltése…
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 opacity-60">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div key={i} className="h-12 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
                        ))}
                      </div>
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            if (slot.isAvailable) {
                              setSelectedSlot(slot);
                              setCurrentStep('info');
                            }
                          }}
                          disabled={!slot.isAvailable}
                          className={`
                            px-4 py-3 rounded-lg border text-sm font-medium transition-all
                            ${slot.isAvailable
                              ? 'bg-white dark:bg-gray-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-primary-300 dark:hover:border-primary-500 hover:shadow-md'
                              : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                            }
                          `}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <ClockIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">
                        Ezen a napon nincs elérhető időpont
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentStep('date')}
                        className="mt-4"
                      >
                        Másik dátum választása
                      </Button>
                    </div>
                  )}

                  {availableSlots.length > 0 && (
                    <div className="flex justify-center">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentStep('date')}
                      >
                        Vissza a dátumokhoz
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Adatok megadása */}
              {currentStep === 'info' && selectedSlot && (
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">
                      Adatok megadása
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {selectedDate?.toLocaleDateString('hu-HU')} - {selectedSlot.time}
                    </p>
                  </div>

                  <div className="space-y-4">
                    {settings.collectInfo.name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Név *
                        </label>
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Teljes név"
                            required
                          />
                        </div>
                      </div>
                    )}

                    {settings.collectInfo.email && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email cím *
                        </label>
                        <div className="relative">
                          <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="email@example.com"
                            required
                          />
                        </div>
                      </div>
                    )}

                    {settings.collectInfo.phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Telefonszám
                        </label>
                        <div className="relative">
                          <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="+36 30 123 4567"
                          />
                        </div>
                      </div>
                    )}

                    {settings.collectInfo.message && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Üzenet
                        </label>
                        <div className="relative">
                          <ChatBubbleLeftRightIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <textarea
                            value={formData.message}
                            onChange={(e) => handleInputChange('message', e.target.value)}
                            rows={3}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                            placeholder="További információk..."
                          />
                        </div>
                      </div>
                    )}

                    {/* Találkozó formája: hibridnél választható, különben rögzített */}
                    {settings.meetingOptions?.enableOnlineMeeting && settings.meetingOptions?.defaultMeetingType !== 'hybrid' && (
                      settings.meetingOptions?.defaultMeetingType === 'online' ? (
                        <div className="flex items-start gap-3 p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
                          <VideoCameraIcon className="w-5 h-5 text-primary-700 dark:text-primary-300 flex-shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium text-primary-900 dark:text-primary-100">Online találkozó</div>
                            <div className="text-primary-800/80 dark:text-primary-200/80">A csatlakozási linket a megerősítő e-mailben küldjük.</div>
                            {settings.meetingOptions?.meetingInstructions && <div className="mt-1 text-primary-800 dark:text-primary-200">{settings.meetingOptions.meetingInstructions}</div>}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                          <MapPinIcon className="w-5 h-5 text-gray-600 dark:text-gray-300 flex-shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium text-gray-900 dark:text-gray-100">Személyes találkozó</div>
                            <div className="text-gray-600 dark:text-gray-400">A helyszín részleteit a megerősítő e-mailben küldjük.</div>
                          </div>
                        </div>
                      )
                    )}
                    {settings.meetingOptions?.enableOnlineMeeting && settings.meetingOptions?.defaultMeetingType === 'hybrid' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          Találkozó formája *
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => handleInputChange('meetingType', 'in_person')}
                            className={`
                              relative flex items-center space-x-3 p-4 border rounded-lg transition-all duration-200
                              ${formData.meetingType === 'in_person'
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-500 ring-opacity-20'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-700'
                              }
                            `}
                          >
                            <MapPinIcon className={`
                              h-5 w-5 
                              ${formData.meetingType === 'in_person' ? 'text-primary-600' : 'text-gray-400'}
                            `} />
                            <div className="text-left">
                              <div className={`
                                text-sm font-medium
                                ${formData.meetingType === 'in_person' ? 'text-primary-900 dark:text-primary-100' : 'text-gray-900 dark:text-gray-100'}
                              `}>
                                Személyes találkozó
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Irodában vagy megbeszélt helyen
                              </div>
                            </div>
                            {formData.meetingType === 'in_person' && (
                              <CheckCircleIcon className="h-5 w-5 text-primary-600 absolute top-2 right-2" />
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleInputChange('meetingType', 'online')}
                            className={`
                              relative flex items-center space-x-3 p-4 border rounded-lg transition-all duration-200
                              ${formData.meetingType === 'online'
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-500 ring-opacity-20'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-700'
                              }
                            `}
                          >
                            <VideoCameraIcon className={`
                              h-5 w-5 
                              ${formData.meetingType === 'online' ? 'text-primary-600' : 'text-gray-400'}
                            `} />
                            <div className="text-left">
                              <div className={`
                                text-sm font-medium
                                ${formData.meetingType === 'online' ? 'text-primary-900 dark:text-primary-100' : 'text-gray-900 dark:text-gray-100'}
                              `}>
                                Online találkozó
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Video hívás vagy telefonos megbeszélés
                              </div>
                            </div>
                            {formData.meetingType === 'online' && (
                              <CheckCircleIcon className="h-5 w-5 text-primary-600 absolute top-2 right-2" />
                            )}
                          </button>
                        </div>
                        
                        {/* Meeting instrukciók megjelenítése */}
                        {formData.meetingType === 'online' && settings.meetingOptions?.meetingInstructions && (
                          <div className="mt-3 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
                            <p className="text-sm text-primary-800 dark:text-primary-200">
                              <strong>Fontos:</strong> {settings.meetingOptions.meetingInstructions}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep('time')}
                    >
                      Vissza
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSubmit}
                      disabled={isSubmitting || !formData.name || !formData.email}
                      className="flex items-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          <span>Foglalás...</span>
                        </>
                      ) : (
                        <span>Foglalás megerősítése</span>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Sikeres foglalás */
          <div className="bg-white dark:bg-[#12151c] rounded-xl border border-gray-200 dark:border-[#2a2f3c] shadow-sm p-8 text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-50 mb-4">
              Foglalás sikeres!
            </h1>
            <div className="space-y-2 text-gray-600 dark:text-gray-400 mb-6">
              <p><strong>Dátum:</strong> {selectedDate?.toLocaleDateString('hu-HU')}</p>
              <p><strong>Időpont:</strong> {selectedSlot?.time}</p>
              <p><strong>Időtartam:</strong> {settings.duration} perc</p>
              {settings.requireConfirmation && (
                <p className="text-yellow-600 font-medium">
                  A foglalás megerősítésre vár
                </p>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Visszaigazoló emailt küldtünk a megadott címre.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicBookingPage;
